import React, { createContext, useContext, useEffect, useState, type FC } from 'react';
import { Button, DatePicker, Divider, Input, Select, type ModalProps } from 'antd';
import { useSelector } from 'react-redux';
import type { RootDispatch, RootState } from '@/components/WellLoggingOperations/types';
import { useDispatch } from 'react-redux';
import type { CreateProjectFundRequest, UpdateProjectFundRequest } from '@/api/api.v2';
import { api, type ProjectFundWellInfo } from '@/api/api.v2';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import type { WellForm, WellInfoContextType } from './types';
import { CustomDoubleInput, CustomInput, CustomSelect } from './ui/customInputs';
import { LoadingOutlined, PlusOutlined } from '@ant-design/icons';
import Modal from '@/shared/Modal';
import { normalizeParams, useItemParams, validateWellInfo } from './utils';
import Cookies from 'js-cookie';

const WellInfoContext = createContext<WellInfoContextType | null>(null);

export const useWellInfo = () => {
    const ctx = useContext(WellInfoContext);
    if (!ctx) throw new Error('useWellInfo must be inside WellInfoProvider');
    return ctx;
};

export const WellInfoProvider = WellInfoContext.Provider;

export const CreateWellModal: FC<
    ModalProps & {
        addWell?: (well: ProjectFundWellInfo) => void;
        withoutContext?: boolean;
    }
> = ({ addWell, withoutContext, ...modalProps }) => {
    const {
        projectFondsData,
        currentExplorationZones,
        currentFields,
        optionsMap,
        openCreateWellModal,
        activeProjectFond,
        addedPoint,
    } = useSelector((state: RootState) => state.projectFund);
    //@ts-ignore
    const dispatch = useDispatch<RootDispatch>();
    const {
        getInfo,
        getFieldsInfo,
        createProject,
        updateProject,
        setOpenCreateWellModal,
        setActiveProjectFond,
        getAllProjects,
        setCustomOptions,
    } = dispatch.projectFund;
    const params = useItemParams();
    const initialFieldId = params.fieldId ?? null;
    const initialFieldTitle = currentFields?.fields?.find(({ id }) => id === initialFieldId)?.title;

    const normalizedParams = normalizeParams(params);
    const currentUsername = Cookies.get('username') as string;

    const onClose = () => setOpenCreateWellModal(false);

    const [optionsLoading, setOptionsLoading] = useState(false);
    const [highlightError, setHighlight] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [wellNumberError, setWellNumberError] = useState(false);
    const [wellInfo, setWellInfo] = useState<WellForm>({
        FieldTitle: initialFieldTitle ?? '',
        fieldId: initialFieldId,
        explorationZoneId: null,
        explorationZone: null,
        mineNumber: null,
        mineId: null,
        wellNumber: '',
        oilFieldId: null,
        stratum: null,
        wellboreValuesId: null,
        developmentIconId: null,
        licenseZoneId: null,
        kupolId: null,
        kupol: null,
        areaId: null,
        area: null,
        stratumIntersectX: Number(addedPoint?.released_x.toFixed(2)) ?? null,
        stratumIntersectY: Number(addedPoint?.released_y.toFixed(2)) ?? null,
        bottomHoleX:
            addedPoint?.pressed_x !== addedPoint?.released_x || addedPoint?.pressed_y !== addedPoint?.released_y
                ? (Number(addedPoint?.pressed_x.toFixed(2)) ?? null)
                : null,
        bottomHoleY:
            addedPoint?.pressed_x !== addedPoint?.released_x || addedPoint?.pressed_y !== addedPoint?.released_y
                ? (Number(addedPoint?.pressed_y.toFixed(2)) ?? null)
                : null,
        outFallX: null,
        outFallY: null,
        drillDate: null,
        createdAt: '',
    });
    const [newProjectTitle, setNewProjectTitle] = useState<string>('');
    const [newOption, setNewOption] = useState<string>('');

    const key = (wellInfo?.fieldId ?? wellInfo?.explorationZoneId)!;
    const currentOptions = optionsMap[key];

    useEffect(() => {
        if (activeProjectFond && openCreateWellModal) {
            setOptionsLoading(true);
            void api.map.projectFund
                .getCustomOptions({ projectFundId: activeProjectFond })
                .then(customOptions => {
                    setCustomOptions(customOptions);
                })
                .finally(() => setOptionsLoading(false));
        }
    }, [activeProjectFond]);

    useEffect(() => {
        if (currentFields.fields.length === 0 && openCreateWellModal) {
            getFieldsInfo(params);
        }
    }, [openCreateWellModal]);

    useEffect(() => {
        if (params.fieldId || params.ventureId) {
            getAllProjects(params);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [params.fieldId, params.ventureId]);

    useEffect(() => {
        setWellInfo(prev => ({
            ...prev,
            stratumIntersectX: Number(addedPoint?.released_x.toFixed(2)) ?? null,
            stratumIntersectY: Number(addedPoint?.released_y.toFixed(2)) ?? null,
            bottomHoleX:
                addedPoint?.pressed_x !== addedPoint?.released_x || addedPoint?.pressed_y !== addedPoint?.released_y
                    ? (Number(addedPoint?.pressed_x.toFixed(2)) ?? null)
                    : null,
            bottomHoleY:
                addedPoint?.pressed_x !== addedPoint?.released_x || addedPoint?.pressed_y !== addedPoint?.released_y
                    ? (Number(addedPoint?.pressed_y.toFixed(2)) ?? null)
                    : null,
        }));
    }, [addedPoint]);

    useEffect(() => {
        setWellInfo(prev => ({
            ...prev,
            explorationZoneId: currentExplorationZones.explorationZones[0]?.id,
            fieldId: currentFields.fields[0]?.id,
            FieldTitle: currentFields.fields[0]?.title,
        }));
    }, [currentExplorationZones, currentFields]);

    useEffect(() => {
        if (currentOptions?.areas[0]) {
            setWellInfo(prev => ({
                ...prev,
                areaId: currentOptions.areas[0]?.value,
            }));
        }
    }, [currentOptions?.areas]);

    useEffect(() => {
        const { fieldId, explorationZoneId } = wellInfo;

        const key = fieldId ?? explorationZoneId;

        if (key && !optionsMap[key]) {
            const params: { explorationZoneId?: number; fieldId?: number } = {};
            if (fieldId) {
                params.fieldId = fieldId;
            }
            if (explorationZoneId) {
                params.explorationZoneId = explorationZoneId;
            }
            getInfo(params);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [wellInfo.fieldId, wellInfo.explorationZoneId]);

    const onApply = async () => {
        setIsLoading(true);
        const { isExist } = await api.map.projectFund.validateFormField({
            table: 'well',
            value: wellInfo.wellNumber,
            fieldId: wellInfo.fieldId!,
            areaId: wellInfo.areaId!,
        });
        const hasValidationErrors = Object.values(validateWellInfo(wellInfo)).some(e => e);
        if (isExist || wellInfo.wellNumber === '') {
            setWellNumberError(true);
        }
        if (hasValidationErrors) {
            setHighlight(true);
        } else if (!isExist && wellInfo.wellNumber !== '') {
            if (addWell) {
                //@ts-ignore
                addWell({ ...wellInfo, createdAt: dayjs().format('YYYY-MM-DDTHH:mm:ss') });
            } else {
                if (activeProjectFond) {
                    const { description, securityLevelId, title } = projectFondsData.find(
                        ({ id }) => id === activeProjectFond,
                    )!;
                    const updatedProject: UpdateProjectFundRequest = {
                        ...normalizedParams,
                        id: activeProjectFond,
                        title,
                        securityLevel: securityLevelId,
                        description,
                        user: currentUsername,
                        addedProjectFundWells: [{ ...wellInfo, createdAt: dayjs().format('YYYY-MM-DDTHH:mm:ss') }],
                        changedProjectFundWells: [],
                        deletedProjectFundWellIds: [],
                    };
                    updateProject(updatedProject);
                } else {
                    const newProject: CreateProjectFundRequest = {
                        ...normalizedParams,
                        title: newProjectTitle,
                        securityLevel: 1,
                        description: '',
                        user: currentUsername,
                        wells: [{ ...wellInfo, createdAt: dayjs().format('YYYY-MM-DDTHH:mm:ss') }],
                    };
                    createProject(newProject);
                }
            }
            onClose();
        }
        setIsLoading(false);
    };

    return (
        <Modal
            onOk={onApply}
            onCancel={onClose}
            width="22rem"
            destroyOnHidden
            closable={false}
            title="Добавить проектную скважину"
            onClose={onClose}
            centered={false}
            style={{ top: 'calc(50% - 350px)' }}
            okButtonProps={{ loading: isLoading }}
            open={openCreateWellModal}
            {...modalProps}
        >
            {currentFields?.fields && currentOptions && currentExplorationZones?.explorationZones ? (
                <WellInfoProvider value={{ wellInfo, setWellInfo }}>
                    <div className="p-4 h-[300px] overflow-auto pt-2">
                        {withoutContext ? (
                            <div className="mt-2">
                                <span className="text-xs">Проектный фонд</span>
                                <Select
                                    loading={optionsLoading}
                                    placeholder="Выберите"
                                    className="w-full !mt-1"
                                    value={activeProjectFond}
                                    onChange={setActiveProjectFond}
                                    dropdownRender={menu => (
                                        <>
                                            {menu}
                                            <Divider style={{ margin: '0 0 8px 0' }} />
                                            <div className="flex mx-1 mb-2">
                                                <Input
                                                    size="small"
                                                    placeholder="Введите название"
                                                    value={newOption}
                                                    disabled={newProjectTitle !== ''}
                                                    onChange={e => setNewOption(e.target.value)}
                                                    onKeyDown={e => e.stopPropagation()}
                                                />
                                                <Button
                                                    size="small"
                                                    type="text"
                                                    disabled={newProjectTitle !== ''}
                                                    icon={<PlusOutlined />}
                                                    onClick={() => {
                                                        if (!newOption.trim()) return;
                                                        setNewOption('');
                                                        setNewProjectTitle(newOption);
                                                    }}
                                                />
                                            </div>
                                        </>
                                    )}
                                >
                                    {newProjectTitle !== '' ? (
                                        <Select.Option key={0} value={0} className="text-xs">
                                            {newProjectTitle}
                                        </Select.Option>
                                    ) : null}
                                    {projectFondsData.map(({ id, title }) => (
                                        <Select.Option key={id} value={id} className="text-xs">
                                            {title}
                                        </Select.Option>
                                    ))}
                                </Select>
                            </div>
                        ) : null}
                        <CustomSelect
                            loading={optionsLoading}
                            label="Месторождение"
                            options={currentFields.fields.map(({ id, title }) => ({ label: title, value: id }))}
                            propKey="fieldId"
                        />
                        <CustomSelect
                            loading={optionsLoading}
                            addInput
                            label="Куст (опционально)"
                            options={currentOptions.mines}
                            propKey="mineId"
                            customPropKey="mineNumber"
                        />
                        <CustomInput
                            label="Скважина"
                            placeholder="Введите номер"
                            propKey="wellNumber"
                            resetValidationError={() => setWellNumberError(false)}
                            status={wellNumberError ? 'error' : undefined}
                        />
                        <CustomSelect
                            loading={optionsLoading}
                            addInput
                            label="Объект"
                            options={currentOptions.stratums}
                            propKey="oilFieldId"
                            customPropKey="stratum"
                            highlightError={highlightError}
                        />
                        <CustomSelect
                            loading={optionsLoading}
                            label="Тип ствола"
                            options={currentOptions.wellbores}
                            highlightError={highlightError}
                            propKey="wellboreValuesId"
                        />
                        <CustomSelect
                            loading={optionsLoading}
                            label="Лицензионный участок"
                            options={currentOptions.licenseZones}
                            propKey="licenseZoneId"
                        />
                        <CustomSelect
                            loading={optionsLoading}
                            label="Площадь"
                            options={currentOptions.areas}
                            propKey="areaId"
                            addInput
                            customPropKey="area"
                        />
                        <CustomSelect
                            loading={optionsLoading}
                            label="Разведочные площади/структуры"
                            options={currentExplorationZones.explorationZones.map(({ id, title }) => ({
                                label: title,
                                value: id,
                            }))}
                            //highlightError={highlightError}
                            propKey="explorationZoneId"
                            addInput
                            customPropKey="explorationZone"
                        />
                        <CustomSelect
                            loading={optionsLoading}
                            label="Поднятия/купола"
                            options={currentOptions.kupols}
                            propKey="kupolId"
                            addInput
                            customPropKey="kupol"
                        />
                        <CustomSelect
                            loading={optionsLoading}
                            label="Категория"
                            options={currentOptions.wellTypes}
                            highlightError={highlightError}
                            propKey="developmentIconId"
                        />
                        <CustomDoubleInput
                            label="Коор. пластопересечения"
                            propKey1="stratumIntersectX"
                            propKey2="stratumIntersectY"
                            highlightError={highlightError}
                        />
                        <CustomDoubleInput label="Коор. забоя ГС" propKey1="bottomHoleX" propKey2="bottomHoleY" />
                        <CustomDoubleInput label="Коор. устья" propKey1="outFallX" propKey2="outFallY" />
                        <div className="mt-2">
                            <span className="text-xs">Предпол. бурение</span>
                            <DatePicker
                                className="w-full !mt-1"
                                value={wellInfo.drillDate ? dayjs(wellInfo.drillDate) : null}
                                onChange={(value: Dayjs | null) => {
                                    setWellInfo(prev => {
                                        const drillDate = value ? value.format('YYYY-MM-DD') : '';
                                        return { ...prev, drillDate };
                                    });
                                }}
                                placeholder="Выберите дату"
                            />
                        </div>
                    </div>
                </WellInfoProvider>
            ) : (
                <div className="flex justify-center items-center h-[300px]">
                    <LoadingOutlined style={{ fontSize: '24px' }} />
                </div>
            )}
        </Modal>
    );
};
